/**
 * Compatibility Validator
 *
 * This script validates products on collection pages against compatibility filters.
 * It hides products that don't truly match the selected brand + model + year combination.
 *
 * The problem: Shopify's native metafield filters don't understand relationships.
 * A product compatible with Model X (years 2003-2005) and Model Y (years 2006-2008)
 * will show up when filtering for Model X + Year 2008, which is incorrect.
 *
 * This script reads the actual compatibility JSON from each product and validates
 * that the selected combination is truly valid.
 */

(function() {
  'use strict';

  const FILTER_KEYS = {
    brand: 'filter.p.m.custom.compatible_brands',
    model: 'filter.p.m.custom.compatible_models',
    year: 'filter.p.m.custom.compatible_years'
  };

  const HIDDEN_CLASS = 'compatibility-hidden';

  /**
   * Parse URL parameters to get active compatibility filters
   */
  function getActiveFilters() {
    const params = new URLSearchParams(window.location.search);

    return {
      brand: params.get(FILTER_KEYS.brand),
      model: params.get(FILTER_KEYS.model),
      year: params.get(FILTER_KEYS.year)
    };
  }

  /**
   * Check if any compatibility filters are active
   */
  function hasCompatibilityFilters(filters) {
    return filters.brand || filters.model || filters.year;
  }

  /**
   * Parse the compatibility data from a product card
   */
  function parseCompatibilityData(productCard) {
    const dataAttr = productCard.getAttribute('data-compatibilities');
    if (!dataAttr) {
      return null;
    }

    try {
      // The data is double-encoded (JSON inside HTML attribute)
      // First, the HTML entities are decoded by the browser
      // Then we parse the JSON string that was stored
      const jsonString = dataAttr;

      // Handle the case where the metafield value is a JSON string
      let parsed = JSON.parse(jsonString);

      // If the result is a string, it means it was double-encoded
      if (typeof parsed === 'string') {
        parsed = JSON.parse(parsed);
      }

      return Array.isArray(parsed) ? parsed : null;
    } catch (e) {
      console.warn('Failed to parse compatibility data:', e);
      return null;
    }
  }

  /**
   * Check if a product is compatible with the selected filters
   */
  function isProductCompatible(compatibilities, filters) {
    if (!compatibilities || !Array.isArray(compatibilities)) {
      // If no compatibility data, we can't validate - show the product
      return true;
    }

    // If no filters are active, show all products
    if (!filters.brand && !filters.model && !filters.year) {
      return true;
    }

    // Find matching compatibility entries
    for (const entry of compatibilities) {
      let matches = true;

      // Check brand filter
      if (filters.brand && entry.brand) {
        if (entry.brand.toLowerCase() !== filters.brand.toLowerCase()) {
          matches = false;
        }
      }

      // Check model filter
      if (matches && filters.model && entry.model) {
        if (entry.model.toLowerCase() !== filters.model.toLowerCase()) {
          matches = false;
        }
      }

      // Check year filter
      if (matches && filters.year && entry.years) {
        const yearNum = parseInt(filters.year, 10);
        const years = entry.years.map(y => parseInt(y, 10));
        if (!years.includes(yearNum)) {
          matches = false;
        }
      }

      // If all active filters match this entry, the product is compatible
      if (matches) {
        return true;
      }
    }

    // No compatibility entry matched all the filters
    return false;
  }

  /**
   * Add CSS styles for hiding products
   */
  function addStyles() {
    if (document.getElementById('compatibility-validator-styles')) {
      return;
    }

    const style = document.createElement('style');
    style.id = 'compatibility-validator-styles';
    style.textContent = `
      .${HIDDEN_CLASS} {
        display: none !important;
      }

      /* HUSQVARNA featured product should always be visible */
      .grid-item:has(product-card[data-product-id="${HUSQVARNA_FEATURED_PRODUCT_ID}"]).${HIDDEN_CLASS} {
        display: block !important;
        display: list-item !important;
      }

      .compatibility-validating .grid-item {
        opacity: 0.5;
        pointer-events: none;
      }

      .compatibility-validated .grid-item {
        opacity: 1;
        pointer-events: auto;
        transition: opacity 0.2s ease;
      }
    `;
    document.head.appendChild(style);
  }

  // HUSQVARNA Featured Product ID - never hide this product when HUSQVARNA filter is active
  const HUSQVARNA_FEATURED_PRODUCT_ID = '7242765238357';

  /**
   * Validate all products on the page
   */
  function validateProducts() {
    const filters = getActiveFilters();

    // Only run validation if compatibility filters are active
    if (!hasCompatibilityFilters(filters)) {
      return;
    }

    const productGrid = document.getElementById('product-grid');
    if (!productGrid) {
      return;
    }

    // Add validating state
    productGrid.classList.add('compatibility-validating');

    // Get all product cards
    const productCards = productGrid.querySelectorAll('product-card[data-product-id]');
    let hiddenCount = 0;

    productCards.forEach(card => {
      const gridItem = card.closest('.grid-item');
      if (!gridItem) return;

      const productId = card.getAttribute('data-product-id');

      // CRITICAL: Never hide HUSQVARNA featured product (ID: 7242765238357)
      // when HUSQVARNA brand filter is active
      if (filters.brand && filters.brand.toUpperCase() === 'HUSQVARNA' && 
          productId === HUSQVARNA_FEATURED_PRODUCT_ID) {
        gridItem.classList.remove(HIDDEN_CLASS);
        return;
      }

      const compatibilities = parseCompatibilityData(card);
      const isCompatible = isProductCompatible(compatibilities, filters);

      if (isCompatible) {
        gridItem.classList.remove(HIDDEN_CLASS);
      } else {
        gridItem.classList.add(HIDDEN_CLASS);
        hiddenCount++;
      }
    });

    // Remove validating state and add validated
    productGrid.classList.remove('compatibility-validating');
    productGrid.classList.add('compatibility-validated');

    // Update product count if available
    updateProductCount(productCards.length - hiddenCount);

    // Log validation results for debugging
    if (hiddenCount > 0) {
      console.log(`Compatibility Validator: Hidden ${hiddenCount} products that don't match the exact filter combination.`);
    }
  }

  /**
   * Update the displayed product count
   */
  function updateProductCount(visibleCount) {
    const countElements = document.querySelectorAll('.product-count-text, .products-count');
    countElements.forEach(el => {
      // Try to update the count if it contains a number
      const text = el.textContent;
      const match = text.match(/\d+/);
      if (match) {
        el.textContent = text.replace(/\d+/, visibleCount);
      }
    });
  }

  /**
   * Handle AJAX navigation (facets updates)
   */
  function observeGridChanges() {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (!productGridContainer) {
      return;
    }

    const observer = new MutationObserver((mutations) => {
      // Check if the product grid was updated
      for (const mutation of mutations) {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          // Re-validate after a short delay to ensure DOM is settled
          setTimeout(validateProducts, 100);
          break;
        }
      }
    });

    observer.observe(productGridContainer, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Initialize the validator
   */
  function init() {
    // Only run on collection pages
    if (!window.location.pathname.includes('/collections/')) {
      return;
    }

    // Only run if compatibility filters are active
    const filters = getActiveFilters();
    if (!hasCompatibilityFilters(filters)) {
      return;
    }

    addStyles();

    // Run validation on page load
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        validateProducts();
        observeGridChanges();
      });
    } else {
      validateProducts();
      observeGridChanges();
    }

    // Re-validate when URL changes (for filter updates via History API)
    window.addEventListener('popstate', () => {
      setTimeout(validateProducts, 100);
    });
  }

  // Start the validator
  init();
})();
